import type { PlacementDeptCode } from "./placements";

/**
 * The Training & Placement Cell's five-year aggregate, 2022–2026.
 *
 * THIS IS NOT THE SAME SHAPE AS placements.ts, and the two must not be summed.
 * placements.ts holds the Cell's drive-by-drive sheet for two graduating
 * batches; this holds the Cell's own year-wise totals for five, plus four
 * dimensions the drive sheet does not carry at all — CTC band, gender,
 * company type and branch.
 *
 * They are, however, the SAME RECORD. The 2025 row here matches the sheet
 * exactly: 579 placed, and male+female = 552, which is precisely the
 * gender-attributed subtotal placements.ts documents for that batch. 2026 has
 * since moved on — the sheet caught 596 across 54 recorded drives, this is the
 * finished year at 639.
 *
 * Basis: the Cell states this column as STUDENTS PLACED, not offers. Note that
 * the 2026 male+female sum lands exactly on `placed`, which is the same
 * per-selection signature that led placements.ts to conclude its own sheet
 * counts selections. The label is the Cell's; it is recorded here so the basis
 * can be revisited without re-deriving the argument.
 *
 * Three kinds of gap in the source, carried rather than papered over:
 *
 *   1. CTC bands plus internship miss the stated total by −4 in 2022 and +1 in
 *      2023. The other three years reconcile exactly.
 *   2. The branch column misses it by −8 in 2022 and −6 in 2023. Likewise.
 *   3. male+female falls short by 11 (2023), 10 (2024) and 27 (2025) — the same
 *      unattributed-rows pattern placements.ts documents. Use
 *      genderAttributed(), never `placed`, as the denominator for any split.
 *
 * `internship` is NOT a salary band. It is a separate outcome and is kept out
 * of the CTC ramp wherever it is drawn.
 */

export type CtcBandKey = "lt3" | "b34" | "b45" | "b56" | "b68" | "b812" | "gte12";

/** Ordered low to high; this is an ordinal scale, not a categorical one. */
export const CTC_BANDS: ReadonlyArray<{ key: CtcBandKey; label: string }> = [
  { key: "lt3", label: "< 3 LPA" },
  { key: "b34", label: "3–4 LPA" },
  { key: "b45", label: "4–5 LPA" },
  { key: "b56", label: "5–6 LPA" },
  { key: "b68", label: "6–8 LPA" },
  { key: "b812", label: "8–12 LPA" },
  { key: "gte12", label: "12+ LPA" },
];

export interface PlacementYear {
  /** Graduating batch. */
  year: number;
  /** Students placed, as the Cell states it. */
  placed: number;
  ctc: Record<CtcBandKey, number>;
  /** A separate outcome, not a salary band. */
  internship: number;
  male: number;
  /** male + female may fall short of `placed`; see the header. */
  female: number;
}

export const PLACEMENT_YEARS: PlacementYear[] = [
  {
    year: 2022,
    placed: 668,
    ctc: { lt3: 24, b34: 327, b45: 150, b56: 69, b68: 43, b812: 19, gte12: 4 },
    internship: 28,
    male: 294,
    female: 374,
  },
  {
    year: 2023,
    placed: 452,
    ctc: { lt3: 20, b34: 136, b45: 205, b56: 20, b68: 45, b812: 20, gte12: 0 },
    internship: 7,
    male: 217,
    female: 224,
  },
  {
    year: 2024,
    placed: 444,
    ctc: { lt3: 12, b34: 126, b45: 101, b56: 122, b68: 16, b812: 3, gte12: 13 },
    internship: 51,
    male: 190,
    female: 244,
  },
  {
    year: 2025,
    placed: 579,
    ctc: { lt3: 30, b34: 200, b45: 65, b56: 48, b68: 53, b812: 9, gte12: 0 },
    internship: 174,
    male: 235,
    female: 317,
  },
  {
    year: 2026,
    placed: 639,
    ctc: { lt3: 46, b34: 86, b45: 96, b56: 50, b68: 96, b812: 4, gte12: 9 },
    internship: 252,
    male: 252,
    female: 387,
  },
];

export const TREND_YEARS: number[] = PLACEMENT_YEARS.map((y) => y.year);

export interface BranchSeries {
  code: PlacementDeptCode;
  label: string;
  /** One cell per TREND_YEARS entry; null where the programme did not exist. */
  byYear: Array<number | null>;
}

/**
 * The Cell's branch labels translated to departments.ts codes. Two of these are
 * trivially swappable and mean different departments:
 *
 *   "CS&D" -> CG   Computer Science & Design
 *   "DS"   -> CD   Computer Science & Engineering (Data Science)
 *
 * The mapping is corroborated by the 2026 drive sheet, where CD totals 43
 * against 44 here and CB totals 31 against 31 here.
 */
export const BRANCH_PLACEMENTS: BranchSeries[] = [
  { code: "CS", label: "Computer Science & Engineering", byYear: [181, 123, 141, 204, 192] },
  { code: "IS", label: "Information Science & Engineering", byYear: [152, 105, 92, 173, 126] },
  { code: "EC", label: "Electronics & Communication", byYear: [143, 84, 36, 63, 82] },
  { code: "EE", label: "Electrical & Electronics", byYear: [48, 20, 37, 8, 37] },
  { code: "ME", label: "Mechanical", byYear: [55, 41, 36, 28, 7] },
  { code: "MCA", label: "Master of Computer Applications", byYear: [16, 19, 16, 48, 12] },
  { code: "TX", label: "Textile", byYear: [30, 17, 22, 20, 16] },
  { code: "CV", label: "Civil", byYear: [11, 8, 8, 17, 17] },
  { code: "BT", label: "Biotechnology", byYear: [11, 9, 37, 2, 6] },
  { code: "CH", label: "Chemical", byYear: [13, 20, 19, 16, 4] },
  { code: "AI", label: "Artificial Intelligence & Machine Learning", byYear: [null, null, null, null, 35] },
  { code: "CB", label: "Computer Science & Business Systems", byYear: [null, null, null, null, 31] },
  { code: "CG", label: "Computer Science & Design", byYear: [null, null, null, null, 30] },
  { code: "CD", label: "Computer Science & Engineering (Data Science)", byYear: [null, null, null, null, 44] },
];

export interface CompanyType {
  label: string;
  companies: number;
}

/**
 * Cumulative across 2022–2026, NOT a single year. This is why it never enters
 * the per-batch headline, which keeps the 2026 sheet's 52 distinct employers.
 */
export const COMPANY_TYPES: CompanyType[] = [
  { label: "IT Service & Consulting", companies: 58 },
  { label: "Core Engineering / Manufacturing", companies: 40 },
  { label: "Internship & Skilling Programs", companies: 32 },
  { label: "IT Product", companies: 19 },
  { label: "R&D / Niche Engineering", companies: 15 },
  { label: "Apparel & Textile Export", companies: 13 },
  { label: "Pharma / Biotech / Chemical", companies: 11 },
];

export const COMPANY_TYPE_TOTAL = 188;

export const latestPlacementYear = (): PlacementYear =>
  PLACEMENT_YEARS[PLACEMENT_YEARS.length - 1];

export const placementYear = (year: number): PlacementYear | undefined =>
  PLACEMENT_YEARS.find((y) => y.year === year);

/** Never use `placed` as a gender denominator; several years under-attribute. */
export const genderAttributed = (y: PlacementYear): number => y.male + y.female;

/** Every CTC band plus the internship count. */
export const ctcAccountedFor = (y: PlacementYear): number =>
  CTC_BANDS.reduce((n, b) => n + y.ctc[b.key], 0) + y.internship;

export function branchTotal(year: number): number {
  const i = TREND_YEARS.indexOf(year);
  if (i < 0) return 0;
  return BRANCH_PLACEMENTS.reduce((n, b) => n + (b.byYear[i] ?? 0), 0);
}

export const womenShare = (y: PlacementYear): number =>
  (y.female / genderAttributed(y)) * 100;
